import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';

import { SiteLayout } from '@/components/layout';
import { AboutPage } from '@/pages/about';
import { AdmissionsPage } from '@/pages/admissions';
import { CurrentStudentsPage } from '@/pages/current-students';
import { ExperiencePage } from '@/pages/experience';
import { ApplyPage } from '@/pages/apply';
import { HomePage } from '@/pages/home';
import { NewsPage } from '@/pages/news';
import { NewsDetailPage } from '@/pages/news-detail';
import { StudentAchievementRatesPage } from '@/pages/student-achievement-rates';
import { OnlineIntroPage } from '@/pages/online-intro';
import { PartnersPage } from '@/pages/partners';
import { LearningCenterDetailPage } from '@/pages/partners/learning-center-detail';
import { ProgrammeDetailPage } from '@/pages/programme-detail';
import { ProgrammesPage } from '@/pages/programmes';
import { ShowcasePage } from '@/pages/showcase';

// The legal pages carry large bodies of verbatim clause text, so they are
// code-split and loaded on demand rather than bundled into the main chunk.
const TermsPage = lazy(() =>
  import('@/pages/legal/terms').then((m) => ({ default: m.TermsPage })),
);
const ComplaintsPage = lazy(() =>
  import('@/pages/legal/complaints').then((m) => ({ default: m.ComplaintsPage })),
);
const CookiesPage = lazy(() =>
  import('@/pages/legal/cookies').then((m) => ({ default: m.CookiesPage })),
);

/**
 * Routes. `/` is the premium home composition and `/showcase` is the component
 * library (each carries its own shell). Standard content pages render inside the
 * shared `SiteLayout` (nav + footer). The static /programmes/online-learning
 * route is declared before the dynamic /programmes/:slug so the introduction
 * page is never swallowed by the slug matcher.
 */
export const router = createBrowserRouter([
  { path: '/', element: <HomePage /> },
  { path: '/showcase', element: <ShowcasePage /> },
  {
    element: <SiteLayout />,
    children: [
      { path: '/programmes', element: <ProgrammesPage /> },
      { path: '/programmes/online-learning', element: <OnlineIntroPage /> },
      { path: '/programmes/:slug', element: <ProgrammeDetailPage /> },
      { path: '/admissions', element: <AdmissionsPage /> },
      { path: '/experience', element: <ExperiencePage /> },
      {
        path: '/experience/student-achievement-rates',
        element: <StudentAchievementRatesPage />,
      },
      { path: '/about', element: <AboutPage /> },
      { path: '/current-students', element: <CurrentStudentsPage /> },
      { path: '/partners', element: <PartnersPage /> },
      {
        path: '/partners/learning-centers/:slug',
        element: <LearningCenterDetailPage />,
      },
      { path: '/news', element: <NewsPage /> },
      { path: '/news/:slug', element: <NewsDetailPage /> },
      // "Apply now" / "Start your application" CTAs resolve here — a real page
      // whose enquiry form is the application entry point.
      { path: '/apply', element: <ApplyPage /> },
      {
        path: '/legal/terms',
        element: (
          <Suspense fallback={null}>
            <TermsPage />
          </Suspense>
        ),
      },
      {
        path: '/legal/complaints',
        element: (
          <Suspense fallback={null}>
            <ComplaintsPage />
          </Suspense>
        ),
      },
      {
        path: '/legal/cookies',
        element: (
          <Suspense fallback={null}>
            <CookiesPage />
          </Suspense>
        ),
      },
    ],
  },
  { path: '*', element: <HomePage /> },
]);
